// INSTISSP-1819 — manages the short-lived token PPA mints for calling SRMS directly (INSTISSP-1808).
//
// The token lives ONLY in this module's memory: never a cookie, never localStorage. The PPA login cookie
// is what authorizes minting; the token is a disposable derived credential that only ever goes to SRMS.
//
// Lifetime: PPA issues a 1-hour token. We renew 5 minutes before it expires (~55 min in), scheduled from the
// token's real `expiresAt` rather than a fixed interval, so a throttled background tab or clock drift can't
// leave a stale token in place. We also re-check when the tab becomes visible again (browsers pause timers in
// hidden tabs). A single in-flight refresh is shared by all callers.
import axios from 'axios';
import { url } from '../../../utils/urls'; // the portal's url (spreads iui sharedURLs) — has baseURL, ppa.url and srms

// PPA endpoint that mints the SRMS token from the logged-in session (INSTISSP-1808).
const MINT_URL = `${url.ppa.url}/v1/auth/srms-token`;

// Renew this long before expiry. Also the staleness threshold for getToken() and the visibility re-check.
const RENEW_SKEW_MS = 5 * 60 * 1000;

let token = null;
let expiresAtMs = 0;          // absolute expiry in ms since epoch (from the mint response's `expiresAt`)
let refreshPromise = null;    // the single in-flight mint request, shared by concurrent callers
let renewalTimer = null;

const nowMs = () => Date.now();
const isFresh = () => token !== null && expiresAtMs - nowMs() > RENEW_SKEW_MS;

// Default reaction when PPA says we're not logged in (the 24h cookie expired): send the user to sign-in,
// same redirect the session sign-out uses. Overridable so tests / callers can intercept it.
let onAuthLost = () => {
    if (typeof window !== 'undefined') {
        window.open(`https://publisher.${url.baseURL}/auth/signin`, '_self');
    }
};

export const setOnAuthLost = fn => { onAuthLost = fn; };

/**
 * Returns a valid SRMS bearer token, refreshing if the cached one is missing or within RENEW_SKEW of expiry.
 * Concurrent callers await the same in-flight refresh.
 */
export const getSrmsToken = () => (isFresh() ? Promise.resolve(token) : refresh());

/**
 * Forces a mint. Shared in-flight promise so a burst of calls (e.g. a dashboard firing several requests) makes
 * one PPA call. A 401 here means the login cookie is gone → clear and hand off to onAuthLost.
 */
export const refresh = () => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = axios
        .get(MINT_URL, { withCredentials: true })
        .then(({ data }) => {
            token = data.token;
            // Prefer the server's expiry; fall back to decoding exp; last resort assume ~55 min of life.
            expiresAtMs = data.expiresAt ? data.expiresAt * 1000 : (decodeExp(token) || nowMs() + 55 * 60 * 1000);
            scheduleRenewal();
            return token;
        })
        .catch(error => {
            const status = error?.response?.status;
            if (status === 401 || status === 403) {
                clearSrmsToken();
                onAuthLost();
            }
            throw error;
        })
        .finally(() => { refreshPromise = null; });
    return refreshPromise;
};

const scheduleRenewal = () => {
    if (renewalTimer) clearTimeout(renewalTimer);
    const delay = Math.max(expiresAtMs - nowMs() - RENEW_SKEW_MS, 0);
    // Background renewal: if it fails (e.g. transient network), stay quiet — the next getSrmsToken() retries,
    // and a real user action surfaces any auth loss. Don't sign the user out from a silent timer.
    renewalTimer = setTimeout(() => { refresh().catch(() => {}); }, delay);
};

/** Drop the token and cancel the renewal timer. Call from the sign-out flow. */
export const clearSrmsToken = () => {
    token = null;
    expiresAtMs = 0;
    if (renewalTimer) { clearTimeout(renewalTimer); renewalTimer = null; }
};

// A hidden tab's renewal timer is throttled/paused, so re-check on return to foreground.
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && token !== null && !isFresh()) {
            refresh().catch(() => {});
        }
    });
}

// Best-effort read of `exp` (seconds) from a JWT payload, without verifying the signature (the client can't).
const decodeExp = jwt => {
    try {
        const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload.exp ? payload.exp * 1000 : null;
    } catch (e) {
        return null;
    }
};
